import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface AddTenantDialogProps {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
  onTenantAdded: () => void;
}

const AddTenantDialog: React.FC<AddTenantDialogProps> = ({
  customerId,
  isOpen,
  onClose,
  onTenantAdded,
}) => {
  const [tenantName, setTenantName] = useState('');
  const [microsoftTenantId, setMicrosoftTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setTenantName('');
    setMicrosoftTenantId('');
    setClientId('');
    setClientSecret('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // It's better to call an edge function to handle vault secret creation.
      const { error } = await supabase.functions.invoke('create-microsoft-tenant', {
        body: {
          customerId,
          tenantName,
          microsoftTenantId,
          clientId,
          clientSecret,
        },
      });

      if (error) {
        // Try to parse the error message if it's a JSON string
        let errorMessage = 'Não foi possível adicionar o tenant.';
        try {
          const errorObj = JSON.parse(error.message);
          if (errorObj.error) {
            errorMessage = errorObj.error;
          }
        } catch (e) {
            errorMessage = error.message;
        }
        throw new Error(errorMessage);
      }

      toast.success('Tenant adicionado com sucesso!');
      onTenantAdded();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Erro ao adicionar tenant:', error);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { onClose(); resetForm(); } }}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Adicionar Microsoft Tenant</DialogTitle>
            <DialogDescription>
              Insira os detalhes do tenant. As credenciais serão armazenadas de forma segura.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="tenantName" className="text-right">
                Nome
              </Label>
              <Input
                id="tenantName"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="microsoftTenantId" className="text-right">
                Tenant ID
              </Label>
              <Input
                id="microsoftTenantId"
                value={microsoftTenantId}
                onChange={(e) => setMicrosoftTenantId(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="clientId" className="text-right">
                Client ID
              </Label>
              <Input
                id="clientId"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="clientSecret" className="text-right">
                Client Secret
              </Label>
              <Input
                id="clientSecret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
                <Button type="button" variant="outline" onClick={resetForm}>
                    Cancelar
                </Button>
            </DialogClose>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTenantDialog;
